/**
 * TanStack Query hooks, one set per resource. Reads use `useQuery`; writes use
 * `useMutation` and invalidate the relevant keys. All go through the active
 * client from {@link useApi}, so they work identically against mocks or the API.
 */
import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';
import { useApi } from './api-context';
import { queryKeys } from './query-keys';
import type {
  AgentPersonaCreate,
  AgentPersonaPut,
  AgentsListing,
  AgentsResponse,
  AiModelSettings,
  AiModelSettingsPatch,
  AppUser,
  AssignableProfile,
  AskInput,
  AskResult,
  BillingCheckoutInput,
  BillingPaymentsResponse,
  BillingPlansResponse,
  BillingReferrals,
  BillingSubscription,
  Conversation,
  ConversationDetail,
  ConversationListParams,
  ConversationPage,
  ConversationPatch,
  CurrentUser,
  DashboardSummary,
  Deck,
  DeckInput,
  DeckUpdateInput,
  Dependent,
  DependentCode,
  DependentCreate,
  DependentPatch,
  DependentReminder,
  DependentReminderCreate,
  DependentRemindersResponse,
  DependentsResponse,
  Document,
  DocumentVisibility,
  EnrichDocumentInput,
  ExamBoardsResponse,
  FeedbackInput,
  FeedbackResult,
  IngestionRun,
  IngestionRunsPage,
  KnowledgeArea,
  GeneratedImageAsset,
  MarketingPopup,
  MarketingPopupPatch,
  SiteContent,
  SiteContentResponse,
  SocialLibrary,
  SocialPost,
  SocialPostInput,
  SocialSettings,
  MetricsCost,
  MetricsOverview,
  ModerationItem,
  ModerationItemDetail,
  ModerationListParams,
  MyPersona,
  MyPersonaCreated,
  MyPersonasResponse,
  Page,
  PageParams,
  PersonaDocument,
  PersonaDocumentsParams,
  PersonaUserCreate,
  PersonaUserPut,
  PreferencesPatch,
  SignupContextInput,
  Profile,
  ProfileCreate,
  ProfileDeleteInput,
  ProfileDeletionImpact,
  ProfilePatch,
  QAInteraction,
  ReindexInput,
  SiteChatInput,
  SiteChatReply,
  TaxonomyArea,
  TaxonomyAreaCreate,
  TaxonomyNode,
  TaxonomyNodePatch,
  TaxonomyResponse,
  TaxonomySubareaCreate,
  Training,
  TrainingInput,
  TranscriptionResult,
  UserInput,
  UserPatch,
  UUID,
  WhatsAppGroup,
  WhatsAppGroupAgentCandidates,
  WhatsAppGroupAgentsChoice,
  WhatsAppGroupCode,
  WhatsAppGroups,
  WhatsAppLink,
  WhatsAppLinkStarted,
} from './types';

// ---- Identity --------------------------------------------------------------

export function useMe(): UseQueryResult<CurrentUser> {
  const api = useApi();
  return useQuery({ queryKey: queryKeys.me, queryFn: () => api.getMe(), staleTime: 300_000 });
}

/**
 * As bancas que o leitor pode escolher.
 *
 * A consulta só SAI quando o perfil tem o recurso: a API responde uma lista
 * vazia para quem não tem, e pedir por ela seria uma viagem para descobrir o que
 * `/v1/me` já disse. Um `profile` explícito (leitura de administrador) dispensa
 * essa condição — ali a pergunta é sobre outro perfil, não sobre o do leitor.
 */
export function useExamBoards(profile?: string): UseQueryResult<ExamBoardsResponse> {
  const api = useApi();
  const me = useMe();
  return useQuery({
    queryKey: queryKeys.examBoards.list(profile),
    queryFn: () => api.listExamBoards(profile),
    enabled: profile !== undefined || me.data?.capabilities.features.examBoards === true,
    // O dicionário viaja com o deploy do servidor: dentro de uma sessão ele não
    // muda, e refazer a leitura a cada montagem do seletor seria ruído.
    staleTime: 300_000,
  });
}

/**
 * Grava a persona oficial e/ou a banca do leitor.
 *
 * A resposta é o `/v1/me` inteiro, então ela SEMEIA o cache em vez de pedir a
 * mesma leitura de novo. O roster cai junto porque a voz mudou: é o servidor
 * que resolve quem responde quando ninguém é fixado, e a listagem em cache foi
 * calculada com a voz anterior.
 */
export function useUpdateMyPreferences(): UseMutationResult<CurrentUser, Error, PreferencesPatch> {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (patch: PreferencesPatch) => api.updateMyPreferences(patch),
    onSuccess: (me) => {
      qc.setQueryData(queryKeys.me, me);
      void qc.invalidateQueries({ queryKey: queryKeys.agents.all });
    },
  });
}

/** A resposta já traz a nova URL assinada: todos os avatares leem o mesmo cache. */
export function useUploadMyAvatar(): UseMutationResult<CurrentUser, Error, File> {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (file: File) => api.uploadMyAvatar(file),
    onSuccess: (me) => qc.setQueryData(queryKeys.me, me),
  });
}

/** Exclusão lógica da própria conta; a sessão local é encerrada pelo chamador. */
export function useDeleteMyAccount(): UseMutationResult<void, Error, void> {
  const api = useApi();
  return useMutation({ mutationFn: () => api.deleteMyAccount() });
}

export function useAcceptSignupContext(): UseMutationResult<CurrentUser, Error, SignupContextInput> {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: SignupContextInput) => api.acceptSignupContext(input),
    onSuccess: (me) => qc.setQueryData(queryKeys.me, me),
  });
}

// ---- Publicidade do site ---------------------------------------------------

export function usePublicMarketingPopup(): UseQueryResult<MarketingPopup> {
  const api = useApi();
  return useQuery({
    queryKey: queryKeys.marketingPopup.public,
    queryFn: () => api.getPublicMarketingPopup(),
    staleTime: 30_000,
    refetchInterval: 60_000,
  });
}

export function useMarketingPopup(): UseQueryResult<MarketingPopup> {
  const api = useApi();
  return useQuery({ queryKey: queryKeys.marketingPopup.admin, queryFn: () => api.getMarketingPopup() });
}

export function useUpdateMarketingPopup(): UseMutationResult<MarketingPopup, Error, MarketingPopupPatch> {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (patch: MarketingPopupPatch) => api.updateMarketingPopup(patch),
    onSuccess: (campaign) => {
      qc.setQueryData(queryKeys.marketingPopup.admin, campaign);
      qc.setQueryData(queryKeys.marketingPopup.public, campaign);
    },
  });
}

export function useUploadMarketingPopupImage(): UseMutationResult<MarketingPopup, Error, File> {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (file: File) => api.uploadMarketingPopupImage(file),
    onSuccess: (campaign) => {
      qc.setQueryData(queryKeys.marketingPopup.admin, campaign);
      qc.setQueryData(queryKeys.marketingPopup.public, campaign);
    },
  });
}

export function useGenerateMarketingImage(): UseMutationResult<
  GeneratedImageAsset,
  Error,
  { prompt: string; includeLogo: boolean }
> {
  const api = useApi();
  return useMutation({ mutationFn: (input) => api.generateMarketingImage(input) });
}

export function usePublicSiteContent(): UseQueryResult<SiteContentResponse> {
  const api = useApi();
  return useQuery({
    queryKey: queryKeys.siteContent.public,
    queryFn: () => api.getPublicSiteContent(),
    staleTime: 60_000,
  });
}

export function useSiteContent(): UseQueryResult<SiteContentResponse> {
  const api = useApi();
  return useQuery({ queryKey: queryKeys.siteContent.admin, queryFn: () => api.getSiteContent() });
}

export function useUpdateSiteContent(): UseMutationResult<SiteContentResponse, Error, SiteContent> {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (content) => api.updateSiteContent(content),
    onSuccess: (content) => {
      qc.setQueryData(queryKeys.siteContent.admin, content);
      qc.setQueryData(queryKeys.siteContent.public, content);
    },
  });
}

export function useSocialLibrary(): UseQueryResult<SocialLibrary> {
  const api = useApi();
  return useQuery({ queryKey: queryKeys.socialLibrary, queryFn: () => api.getSocialLibrary() });
}

export function useCreateSocialPost(): UseMutationResult<SocialPost, Error, SocialPostInput> {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input) => api.createSocialPost(input),
    onSuccess: () => qc.invalidateQueries({ queryKey: queryKeys.socialLibrary }),
  });
}

export function useUpdateSocialSettings(): UseMutationResult<SocialSettings, Error, SocialSettings> {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (settings) => api.updateSocialSettings(settings),
    onSuccess: (settings) => {
      qc.setQueryData<SocialLibrary>(queryKeys.socialLibrary, (current) => current ? { ...current, settings } : undefined);
    },
  });
}

// ---- Profiles --------------------------------------------------------------

/** Every profile with its quotas and features (admin). */
/**
 * O catálogo de perfis (admin). `enabled: false` deixa a leitura parada: uma
 * tela de todos os papéis a pede só para quem pode lê-la, em vez de um 403.
 */
export function useProfiles(
  options: { enabled?: boolean } = {},
): UseQueryResult<{ profiles: Profile[] }> {
  const api = useApi();
  return useQuery({
    queryKey: queryKeys.profiles.all,
    queryFn: () => api.listProfiles(),
    staleTime: 60_000,
    enabled: options.enabled ?? true,
  });
}

/**
 * The profiles a member may choose from. A separate query from
 * {@link useProfiles} because it is a separate ENDPOINT: this one every role may
 * read, and it carries no quota — so a picker never has to hold numbers that are
 * admin-only.
 *
 * The admin's Usuários column deliberately does NOT use it: a picker lists what
 * may be CHOSEN, and that column also has to render what an account already
 * holds — a retired profile, or one meant for dependents. Reading the whole
 * catalogue there is what keeps the cell from showing somebody else's quota.
 */
export function useAssignableProfiles(
  to: 'user' | 'dependent' = 'user',
): UseQueryResult<{ profiles: AssignableProfile[] }> {
  const api = useApi();
  return useQuery({
    queryKey: [...queryKeys.profiles.all, 'assignable', to] as const,
    queryFn: () => api.listAssignableProfiles(to),
    staleTime: 300_000,
  });
}

/**
 * What every profile write invalidates: the list itself and `/me` — the quotas
 * the console shows the signed-in person come from their profile's row, so an
 * edit to it is an edit to what they are told they may do.
 */
function useProfileInvalidation(): () => void {
  const qc = useQueryClient();
  return () => {
    void qc.invalidateQueries({ queryKey: queryKeys.profiles.all });
    void qc.invalidateQueries({ queryKey: queryKeys.me });
  };
}

export function useCreateProfile(): UseMutationResult<Profile, Error, ProfileCreate> {
  const api = useApi();
  const invalidate = useProfileInvalidation();
  return useMutation({ mutationFn: (input: ProfileCreate) => api.createProfile(input), onSuccess: invalidate });
}

export function useUpdateProfile(): UseMutationResult<
  Profile,
  Error,
  { slug: string; patch: ProfilePatch }
> {
  const api = useApi();
  const invalidate = useProfileInvalidation();
  return useMutation({
    mutationFn: ({ slug, patch }: { slug: string; patch: ProfilePatch }) =>
      api.updateProfile(slug, patch),
    onSuccess: invalidate,
  });
}

/**
 * O que deletar `slug` moveria, lido sempre fresco: a confirmação cita esses
 * números, e um cache de minutos atrás poderia prometer mover menos do que move.
 * `null` deixa a leitura parada enquanto nenhum perfil está sendo deletado.
 */
export function useProfileDeletionImpact(
  slug: string | null,
): UseQueryResult<ProfileDeletionImpact> {
  const api = useApi();
  return useQuery({
    queryKey: queryKeys.profiles.deletionImpact(slug ?? ''),
    queryFn: () => api.getProfileDeletionImpact(slug!),
    enabled: slug !== null,
    staleTime: 0,
    // Um 409 (o `padrao`) ou um 404 não mudam numa segunda tentativa.
    retry: false,
  });
}

/**
 * Deletar um perfil mexe em tudo que apontava para ele: as contas passam para o
 * `padrao` (a lista de usuários e o `/me`), as áreas mudam de perfil (a árvore,
 * o vocabulário), os agentes mudam de elenco e os dependentes, de perfil.
 */
export function useDeleteProfile(): UseMutationResult<void, Error, ProfileDeleteInput> {
  const api = useApi();
  const qc = useQueryClient();
  const invalidate = useProfileInvalidation();
  return useMutation({
    mutationFn: ({ slug, dependentsTo }: ProfileDeleteInput) =>
      api.deleteProfile(slug, { dependentsTo }),
    onSuccess: (_, { slug }) => {
      // O impacto de um perfil que não existe mais só responderia 404: sai do
      // cache antes que a invalidação do prefixo o peça de novo.
      qc.removeQueries({ queryKey: queryKeys.profiles.deletionImpact(slug) });
      invalidate();
      for (const queryKey of [
        queryKeys.users.all,
        queryKeys.taxonomy.all,
        queryKeys.areas.all,
        queryKeys.agents.all,
        queryKeys.myDependents.all,
      ]) {
        void qc.invalidateQueries({ queryKey });
      }
    },
  });
}

// ---- Taxonomy --------------------------------------------------------------

/** The platform tree (admin), whole or narrowed to one profile. */
export function useTaxonomy(profile?: string): UseQueryResult<TaxonomyResponse> {
  const api = useApi();
  return useQuery({
    queryKey: queryKeys.taxonomy.list(profile),
    queryFn: () => api.getTaxonomy(profile),
    staleTime: 60_000,
  });
}

/**
 * What every taxonomy write invalidates. Three families, because one edit moves
 * three things: the tree the admin is looking at, the area VOCABULARY every
 * picker in the console reads, and the document listings — a row shows the label
 * of the area it is filed in, so a rename that stopped at the tree would leave
 * the old name on screen everywhere else.
 */
function useTaxonomyInvalidation(): () => void {
  const qc = useQueryClient();
  return () => {
    void qc.invalidateQueries({ queryKey: queryKeys.taxonomy.all });
    void qc.invalidateQueries({ queryKey: queryKeys.areas.all });
    void qc.invalidateQueries({ queryKey: queryKeys.documents.all });
  };
}

export function useCreateTaxonomyArea(): UseMutationResult<TaxonomyArea, Error, TaxonomyAreaCreate> {
  const api = useApi();
  const invalidate = useTaxonomyInvalidation();
  return useMutation({
    mutationFn: (input: TaxonomyAreaCreate) => api.createTaxonomyArea(input),
    onSuccess: invalidate,
  });
}

export function useCreateTaxonomySubarea(): UseMutationResult<
  TaxonomyNode,
  Error,
  { areaSlug: string; input: TaxonomySubareaCreate }
> {
  const api = useApi();
  const invalidate = useTaxonomyInvalidation();
  return useMutation({
    mutationFn: ({ areaSlug, input }: { areaSlug: string; input: TaxonomySubareaCreate }) =>
      api.createTaxonomySubarea(areaSlug, input),
    onSuccess: invalidate,
  });
}

export function usePatchTaxonomyNode(): UseMutationResult<
  TaxonomyNode,
  Error,
  { slug: string; patch: TaxonomyNodePatch }
> {
  const api = useApi();
  const invalidate = useTaxonomyInvalidation();
  return useMutation({
    mutationFn: ({ slug, patch }: { slug: string; patch: TaxonomyNodePatch }) =>
      api.patchTaxonomyNode(slug, patch),
    onSuccess: invalidate,
  });
}

export function useDeleteTaxonomyNode(): UseMutationResult<void, Error, string> {
  const api = useApi();
  const invalidate = useTaxonomyInvalidation();
  return useMutation({
    mutationFn: (slug: string) => api.deleteTaxonomyNode(slug),
    onSuccess: invalidate,
  });
}

// ---- Knowledge -------------------------------------------------------------

/**
 * The knowledge-area vocabulary. Held for five minutes because a taxonomy changes
 * when the base is reorganized, not between two clicks — and every area selector
 * on the page reads this one query, so refetching it is pure churn.
 *
 * Both options are ADMIN narrowings: `profile` slices the platform tree, and
 * `userId` reads ANOTHER user's personal vocabulary (the picker for editing a
 * document of theirs in the inventory). Everybody else already receives their
 * own shelf plus their profile's tree, and passing either would only ask the
 * server for a slice it has already decided — or a 403 it must answer.
 */
export function useAreas(options?: {
  profile?: string;
  userId?: UUID;
}): UseQueryResult<{ areas: KnowledgeArea[] }> {
  const api = useApi();
  return useQuery({
    queryKey: queryKeys.areas.list(options),
    queryFn: () => api.listAreas(options),
    staleTime: 300_000,
  });
}

/** Create one of the reader's personal areas (or a subarea, with `parentSlug`);
 *  the vocabulary is refetched. */
export function useCreateArea(): UseMutationResult<
  KnowledgeArea,
  Error,
  { label: string; parentSlug?: string }
> {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ label, parentSlug }: { label: string; parentSlug?: string }) =>
      api.createArea(label, parentSlug),
    onSuccess: () => qc.invalidateQueries({ queryKey: queryKeys.areas.all }),
  });
}

/** Rename one of the reader's areas. The documents are refetched too: every row
 *  filed there shows the area's label. */
export function useRenameArea(): UseMutationResult<
  KnowledgeArea,
  Error,
  { slug: string; label: string }
> {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ slug, label }: { slug: string; label: string }) => api.renameArea(slug, label),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.areas.all });
      qc.invalidateQueries({ queryKey: queryKeys.documents.all });
    },
  });
}

/**
 * Delete one of the reader's areas. The API never refuses it for content: the
 * documents filed there (or under a subarea, which goes with it) are unfiled
 * back to "Sem área" in the same request — which is why the DOCUMENTS are
 * refetched too, not just the vocabulary.
 */
export function useDeleteArea(): UseMutationResult<void, Error, string> {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (slug: string) => api.deleteArea(slug),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.areas.all });
      qc.invalidateQueries({ queryKey: queryKeys.documents.all });
    },
  });
}

export function useDocuments(params?: PageParams): UseQueryResult<Page<Document>> {
  const api = useApi();
  return useQuery({
    queryKey: queryKeys.documents.list(params),
    queryFn: () => api.listDocuments(params),
  });
}

/** Fetches the signed URL for a document's retained file, on click. */
export function useDocumentDownload(): UseMutationResult<{ url: string }, Error, UUID> {
  const api = useApi();
  return useMutation({ mutationFn: (id: UUID) => api.getDocumentDownloadUrl(id) });
}

/**
 * Reclassifies one document (`PATCH /v1/documents/{id}`); `area: null` unfiles it.
 *
 * Invalidates the areas too, not just the documents: the vocabulary is served
 * WITH a document count per shelf and is cached for five minutes, so moving a
 * document would otherwise leave both the picker's counts and the empty-area hint
 * describing a corpus that no longer exists.
 */
export function useSetDocumentArea(): UseMutationResult<
  Document,
  Error,
  { id: UUID; area: string | null }
> {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, area }: { id: UUID; area: string | null }) => api.setDocumentArea(id, area),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.documents.all });
      qc.invalidateQueries({ queryKey: queryKeys.areas.all });
    },
  });
}

/**
 * Move a document between the reader's own shelf and the shared acervo.
 *
 * Invalidates the same two families a reclassification does, and for a stricter
 * reason: publishing changes WHO can retrieve the document, so a listing (or an
 * area count) still describing the old shelf is not a stale number — it is a
 * wrong answer about who can read what.
 */
export function useSetDocumentVisibility(): UseMutationResult<
  Document,
  Error,
  { id: UUID; visibility: DocumentVisibility }
> {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, visibility }: { id: UUID; visibility: DocumentVisibility }) =>
      api.setDocumentVisibility(id, visibility),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.documents.all });
      qc.invalidateQueries({ queryKey: queryKeys.areas.all });
    },
  });
}

export function useDeleteDocument(): UseMutationResult<void, Error, UUID> {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: UUID) => api.deleteDocument(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: queryKeys.documents.all }),
  });
}

/** Poll interval (ms) while any ingestion run is in flight; false once terminal. */
export const INGESTION_POLL_MS = 4000;

/**
 * Whether the ingestion-runs list should keep polling: true while any run is
 * still in flight.
 *
 * Driven by the envelope's `runningCount`, not by the rows in hand: a batch
 * upload can be larger than a page, and on page 2 of the history every visible
 * run is already terminal — counting those would stop the polling while thirteen
 * files were still being indexed. Extracted so the cadence is unit-testable.
 */
export function ingestionRunsPollInterval(runningCount: number): number | false {
  return runningCount > 0 ? INGESTION_POLL_MS : false;
}

export function useIngestionRuns(params?: PageParams): UseQueryResult<IngestionRunsPage> {
  const api = useApi();
  return useQuery({
    queryKey: queryKeys.ingestionRuns.list(params),
    queryFn: () => api.listIngestionRuns(params),
    // Poll while any run is still in flight so the Ingestão tab reflects progress
    // live; stop once everything is terminal (succeeded/failed).
    refetchInterval: (query) => ingestionRunsPollInterval(query.state.data?.runningCount ?? 0),
  });
}

export function useReindex(): UseMutationResult<IngestionRun, Error, ReindexInput> {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: ReindexInput) => api.reindex(input),
    onSuccess: () => qc.invalidateQueries({ queryKey: queryKeys.ingestionRuns.all }),
  });
}

// ---- Decks -----------------------------------------------------------------

export function useDecks(params?: PageParams): UseQueryResult<Page<Deck>> {
  const api = useApi();
  return useQuery({
    queryKey: queryKeys.decks.list(params),
    queryFn: () => api.listDecks(params),
    // Poll while any deck is still being rendered so the console reflects progress
    // live; stop once everything is terminal (ready/failed).
    refetchInterval: (query) => {
      const items = query.state.data?.items ?? [];
      const pending = items.some((d) => d.status === 'requested' || d.status === 'rendering');
      return pending ? 4000 : false;
    },
  });
}

export function useCreateDeck(): UseMutationResult<Deck, Error, DeckInput> {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: DeckInput) => api.createDeck(input),
    onSuccess: () => qc.invalidateQueries({ queryKey: queryKeys.decks.all }),
  });
}

export function useUpdateDeck(): UseMutationResult<Deck, Error, { id: UUID; input: DeckUpdateInput }> {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, input }: { id: UUID; input: DeckUpdateInput }) => api.updateDeck(id, input),
    onSuccess: () => qc.invalidateQueries({ queryKey: queryKeys.decks.all }),
  });
}

export function useDeckDownload(): UseMutationResult<{ url: string }, Error, UUID> {
  const api = useApi();
  return useMutation({ mutationFn: (id: UUID) => api.getDeckDownloadUrl(id) });
}

// ---- Trainings -------------------------------------------------------------

/** Poll interval (ms) while any training is still in flight; false once terminal. */
export const TRAINING_POLL_MS = 4000;

/**
 * Whether the trainings list should keep polling: true while any item is
 * requested/processing. Extracted so the cadence is unit-testable.
 */
export function trainingsPollInterval(items: Training[]): number | false {
  const pending = items.some((t) => t.status === 'requested' || t.status === 'processing');
  return pending ? TRAINING_POLL_MS : false;
}

export function useTrainings(params?: PageParams): UseQueryResult<Page<Training>> {
  const api = useApi();
  return useQuery({
    queryKey: queryKeys.trainings.list(params),
    queryFn: () => api.listTrainings(params),
    // Poll while any training is still being processed so the console reflects
    // progress live; stop once everything is terminal (ready/failed).
    refetchInterval: (query) => trainingsPollInterval(query.state.data?.items ?? []),
  });
}

export function useCreateTraining(): UseMutationResult<Training, Error, TrainingInput> {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: TrainingInput) => api.createTraining(input),
    onSuccess: () => qc.invalidateQueries({ queryKey: queryKeys.trainings.all }),
  });
}

/**
 * Enrich an already-indexed document from its row on Documentos (`POST
 * /v1/documents/{id}/enrich`): the source is never reindexed, and the pack is
 * derived from the retained file onto the same shelf. Accepted (202), so both
 * the trainings list and the ingestion timeline (which carries the run under
 * the document's title) are refetched.
 */
export function useEnrichDocument(): UseMutationResult<
  Training,
  Error,
  { id: UUID; input: EnrichDocumentInput }
> {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, input }: { id: UUID; input: EnrichDocumentInput }) =>
      api.enrichDocument(id, input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.trainings.all });
      qc.invalidateQueries({ queryKey: queryKeys.ingestionRuns.all });
    },
  });
}

// ---- QA --------------------------------------------------------------------

export function useInteractions(params?: PageParams): UseQueryResult<Page<QAInteraction>> {
  const api = useApi();
  return useQuery({
    queryKey: queryKeys.interactions.list(params),
    queryFn: () => api.listInteractions(params),
  });
}

/** One recorded interaction — the dashboard's "Aguardando revisão" review target. */
export function useInteraction(id: UUID | null): UseQueryResult<QAInteraction> {
  const api = useApi();
  return useQuery({
    queryKey: queryKeys.interactions.detail(id ?? 'none'),
    queryFn: () => api.getInteraction(id as UUID),
    enabled: id !== null,
    // A missing/foreign id is a plain 404 — retrying can never fix it.
    retry: false,
    // A recorded interaction is immutable history; only feedback changes, and
    // that lives elsewhere — no refetch churn while the reviewer reads.
    staleTime: Infinity,
  });
}

export function useAsk(): UseMutationResult<AskResult, Error, AskInput> {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: AskInput) => api.ask(input),
    onSuccess: () =>
      Promise.all([
        qc.invalidateQueries({ queryKey: queryKeys.interactions.all }),
        qc.invalidateQueries({ queryKey: queryKeys.conversations.all }),
        // A resposta gasta a cota mensal do plano: o /me é quem a conta.
        qc.invalidateQueries({ queryKey: queryKeys.me }),
      ]),
  });
}

/** Variables for {@link useSendFeedback}: the answer being rated and the vote. */
export interface SendFeedbackVariables {
  interactionId: UUID;
  input: FeedbackInput;
}

export function useSendFeedback(): UseMutationResult<
  FeedbackResult,
  Error,
  SendFeedbackVariables
> {
  const api = useApi();
  // Nothing to invalidate: the vote is write-only telemetry (no list reads it back).
  return useMutation({
    mutationFn: ({ interactionId, input }: SendFeedbackVariables) =>
      api.sendFeedback(interactionId, input),
  });
}

interface TranscribeAudioVariables {
  audio: Blob;
  mimetype: string;
}

/** The microphone's clip → text. Nothing to invalidate: nothing is stored. */
export function useTranscribeAudio(): UseMutationResult<
  TranscriptionResult,
  Error,
  TranscribeAudioVariables
> {
  const api = useApi();
  return useMutation({
    mutationFn: ({ audio, mimetype }: TranscribeAudioVariables) =>
      api.transcribeAudio(audio, mimetype),
  });
}

// ---- Conversations ---------------------------------------------------------

/**
 * The caller's history (newest first) or a search over it. `enabled` lets the
 * shell hold the read until there is a session — an anonymous page has no
 * history to ask for.
 */
export function useConversations(
  params?: ConversationListParams,
  options: { enabled?: boolean } = {},
): UseQueryResult<ConversationPage> {
  const api = useApi();
  return useQuery({
    queryKey: queryKeys.conversations.list(params),
    queryFn: () => api.listConversations(params),
    enabled: options.enabled ?? true,
    staleTime: 15_000,
  });
}

/** One thread with its messages; `null` is "no thread open" and asks nothing. */
export function useConversation(id: UUID | null): UseQueryResult<ConversationDetail> {
  const api = useApi();
  return useQuery({
    queryKey: queryKeys.conversations.detail(id ?? 'none'),
    queryFn: () => api.getConversation(id as UUID),
    enabled: id !== null,
    // A foreign, archived or missing id is a plain 404 — retrying never fixes it.
    retry: false,
  });
}

/**
 * Renomeia e/ou fixa uma conversa. Os dois mudam o que toda listagem mostra — o
 * título é o texto da linha, a fixação a move — e a ordem é do servidor, então as
 * listas são relidas em vez de remendadas à mão; o cabeçalho da conversa aberta
 * (o detalhe) cai junto, pelo mesmo prefixo.
 */
export function useUpdateConversation(): UseMutationResult<
  Conversation,
  Error,
  { id: UUID; patch: ConversationPatch }
> {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, patch }: { id: UUID; patch: ConversationPatch }) =>
      api.updateConversation(id, patch),
    onSuccess: () => qc.invalidateQueries({ queryKey: queryKeys.conversations.all }),
  });
}

export function useArchiveConversation(): UseMutationResult<void, Error, UUID> {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: UUID) => api.archiveConversation(id),
    onSuccess: (_void, id) => {
      // The detail is gone for good (the API answers 404 from now on): drop it
      // instead of refetching it into an error.
      qc.removeQueries({ queryKey: queryKeys.conversations.detail(id) });
      return qc.invalidateQueries({ queryKey: queryKeys.conversations.all });
    },
  });
}

// ---- Metrics ---------------------------------------------------------------

export function useMetricsOverview(): UseQueryResult<MetricsOverview> {
  const api = useApi();
  return useQuery({
    queryKey: queryKeys.metrics.overview,
    queryFn: () => api.getMetricsOverview(),
    staleTime: 60_000,
  });
}

/** Admin-only on the API: mount it only in a component an admin alone renders. */
export function useMetricsCost(): UseQueryResult<MetricsCost> {
  const api = useApi();
  return useQuery({
    queryKey: queryKeys.metrics.cost,
    queryFn: () => api.getMetricsCost(),
    staleTime: 60_000,
  });
}

// ---- Dashboard -------------------------------------------------------------

/**
 * Recent documents, conversations and personas. One query for the three panels
 * that read it: each panel calls this hook itself and TanStack dedupes them into
 * a single request.
 */
export function useDashboardSummary(): UseQueryResult<DashboardSummary> {
  const api = useApi();
  return useQuery({
    queryKey: queryKeys.dashboard.summary,
    queryFn: () => api.getDashboardSummary(),
    staleTime: 60_000,
  });
}

// ---- Users -----------------------------------------------------------------

export function useUsers(params?: PageParams): UseQueryResult<Page<AppUser>> {
  const api = useApi();
  return useQuery({
    queryKey: queryKeys.users.list(params),
    queryFn: () => api.listUsers(params),
  });
}

export function useCreateUser(): UseMutationResult<AppUser, Error, UserInput> {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: UserInput) => api.createUser(input),
    onSuccess: () => qc.invalidateQueries({ queryKey: queryKeys.users.all }),
  });
}

export function useUpdateUser(): UseMutationResult<
  AppUser,
  Error,
  { id: UUID; patch: UserPatch }
> {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, patch }) => api.updateUser(id, patch),
    onSuccess: () => qc.invalidateQueries({ queryKey: queryKeys.users.all }),
  });
}

export function useDeleteUser(): UseMutationResult<void, Error, UUID> {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: UUID) => api.deleteUser(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: queryKeys.users.all }),
  });
}

export function useResendUserInvite(): UseMutationResult<AppUser, Error, UUID> {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: UUID) => api.resendUserInvite(id),
    // The row itself does not move (it stays pending until the first sign-in),
    // but the listing is refetched anyway: a resend is the moment an admin
    // wants to see whether somebody has meanwhile come in.
    onSuccess: () => qc.invalidateQueries({ queryKey: queryKeys.users.all }),
  });
}

// ---- AI model settings -------------------------------------------------------

export function useAiModelSettings(): UseQueryResult<AiModelSettings> {
  const api = useApi();
  return useQuery({
    queryKey: queryKeys.aiModelSettings,
    queryFn: () => api.getAiModelSettings(),
    staleTime: 60_000,
  });
}

export function useUpdateAiModelSettings(): UseMutationResult<
  AiModelSettings,
  Error,
  AiModelSettingsPatch
> {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (patch: AiModelSettingsPatch) => api.updateAiModelSettings(patch),
    onSuccess: (settings) => qc.setQueryData(queryKeys.aiModelSettings, settings),
  });
}

// ---- Specialist agents -----------------------------------------------------

/**
 * The caller's roster. `profile` is an ADMIN narrowing: a member already
 * receives their own profile's personas plus `padrao`, so passing one would only
 * ask the server for a slice it has already decided.
 */
export function useAgents(profile?: string): UseQueryResult<AgentsListing> {
  const api = useApi();
  return useQuery({
    queryKey: queryKeys.agents.list(profile),
    queryFn: () => api.getAgents(profile),
    staleTime: 60_000,
  });
}

/**
 * What a persona write leaves behind. The response IS the whole roster, so the
 * unfiltered listing is seeded from it; a profile-narrowed entry is only marked
 * stale, because refetching one here would immediately undo that seeding.
 */
function useAgentsWriteResult(): (agents: AgentsResponse) => void {
  const qc = useQueryClient();
  return (agents) => {
    qc.setQueryData(queryKeys.agents.list(), agents);
    void qc.invalidateQueries({ queryKey: queryKeys.agents.all, refetchType: 'none' });
  };
}

export function useUpdateAgent(): UseMutationResult<
  AgentsResponse,
  Error,
  { id: string; payload: AgentPersonaPut }
> {
  const api = useApi();
  const seed = useAgentsWriteResult();
  return useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: AgentPersonaPut }) =>
      api.updateAgent(id, payload),
    onSuccess: seed,
  });
}

export function useCreateAgent(): UseMutationResult<AgentsResponse, Error, AgentPersonaCreate> {
  const api = useApi();
  const seed = useAgentsWriteResult();
  return useMutation({
    mutationFn: (input: AgentPersonaCreate) => api.createAgent(input),
    onSuccess: seed,
  });
}

export function useDeleteAgent(): UseMutationResult<void, Error, string> {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.deleteAgent(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: queryKeys.agents.all }),
  });
}

/**
 * One page of a persona's document candidates. Held briefly: the picker's whole
 * job is to show what is linked RIGHT NOW, and the acervo behind it changes
 * whenever somebody publishes a document.
 */
export function useAgentDocuments(
  id: string | null,
  params: PersonaDocumentsParams,
): UseQueryResult<Page<PersonaDocument>> {
  const api = useApi();
  return useQuery({
    queryKey: queryKeys.agents.documents(id ?? '', params),
    queryFn: () => api.listAgentDocuments(id!, params),
    enabled: id !== null,
    staleTime: 15_000,
  });
}

/**
 * Replace a persona's linked documents. Both families are invalidated: the
 * picker's pages carry a `linked` flag per row, and the roster carries
 * `linkedDocumentCount` — the number the persona's row shows.
 */
export function useSetAgentDocuments(): UseMutationResult<
  { linked: number },
  Error,
  { id: string; documentIds: UUID[] }
> {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, documentIds }: { id: string; documentIds: UUID[] }) =>
      api.setAgentDocuments(id, documentIds),
    onSuccess: (_result, { id }) => {
      void qc.invalidateQueries({ queryKey: queryKeys.agents.documents(id) });
      void qc.invalidateQueries({ queryKey: queryKeys.agents.all });
    },
  });
}

// ---- Personas do próprio leitor --------------------------------------------

/** As personas do leitor, em qualquer status, com o teto do perfil dele. */
export function useMyPersonas(): UseQueryResult<MyPersonasResponse> {
  const api = useApi();
  return useQuery({
    queryKey: queryKeys.myPersonas.all,
    queryFn: () => api.listMyPersonas(),
    staleTime: 30_000,
  });
}

/**
 * O que toda escrita de persona própria deixa para trás. As duas famílias caem
 * juntas e a segunda não é redundante: uma persona aprovada entra no roster que
 * o seletor do chat lê, e uma rejeitada sai dele — o seletor não tem como saber
 * disso olhando só para a lista de Configurações. Os grupos do WhatsApp caem
 * pelo mesmo motivo: criar um agente tira de "Todos" os grupos do dono, ligar,
 * desligar ou apagar um muda as tags "(desligado)" dos cartões e os candidatos
 * do seletor de agentes do grupo.
 */
function useMyPersonasWriteResult(): () => void {
  const qc = useQueryClient();
  return () => {
    void qc.invalidateQueries({ queryKey: queryKeys.myPersonas.all });
    void qc.invalidateQueries({ queryKey: queryKeys.agents.all });
    void qc.invalidateQueries({ queryKey: queryKeys.whatsappGroups });
    void qc.invalidateQueries({ queryKey: queryKeys.whatsappGroupAgents });
  };
}

export function useCreateMyPersona(): UseMutationResult<
  MyPersonaCreated,
  Error,
  PersonaUserCreate
> {
  const api = useApi();
  const settle = useMyPersonasWriteResult();
  return useMutation({
    mutationFn: (input: PersonaUserCreate) => api.createMyPersona(input),
    onSuccess: settle,
  });
}

export function useUpdateMyPersona(): UseMutationResult<
  MyPersona,
  Error,
  { id: string; payload: PersonaUserPut }
> {
  const api = useApi();
  const settle = useMyPersonasWriteResult();
  return useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: PersonaUserPut }) =>
      api.updateMyPersona(id, payload),
    onSuccess: settle,
  });
}

export function useDeleteMyPersona(): UseMutationResult<void, Error, string> {
  const api = useApi();
  const settle = useMyPersonasWriteResult();
  return useMutation({
    mutationFn: (id: string) => api.deleteMyPersona(id),
    onSuccess: settle,
  });
}

/** Uma página de candidatos ao vínculo de uma persona própria. */
export function useMyPersonaDocuments(
  id: string | null,
  params: PersonaDocumentsParams,
): UseQueryResult<Page<PersonaDocument>> {
  const api = useApi();
  return useQuery({
    queryKey: queryKeys.myPersonas.documents(id ?? '', params),
    queryFn: () => api.listMyPersonaDocuments(id!, params),
    enabled: id !== null,
    staleTime: 15_000,
  });
}

export function useSetMyPersonaDocuments(): UseMutationResult<
  { linked: number },
  Error,
  { id: string; documentIds: UUID[] }
> {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, documentIds }: { id: string; documentIds: UUID[] }) =>
      api.setMyPersonaDocuments(id, documentIds),
    onSuccess: (_result, { id }) => {
      void qc.invalidateQueries({ queryKey: queryKeys.myPersonas.documents(id) });
      void qc.invalidateQueries({ queryKey: queryKeys.myPersonas.all });
    },
  });
}

// ---- Fila de moderação (admin) ---------------------------------------------

/** Uma página da fila. Sem `status`, a fila é a de quem espera: `review`. */
export function useModerationPersonas(
  params: ModerationListParams,
): UseQueryResult<Page<ModerationItem>> {
  const api = useApi();
  return useQuery({
    queryKey: queryKeys.moderation.list(params),
    queryFn: () => api.listModerationPersonas(params),
    staleTime: 15_000,
  });
}

/** A linha aberta, com o texto julgado. Só é buscada quando o Sheet abre. */
export function useModerationPersona(id: string | null): UseQueryResult<ModerationItemDetail> {
  const api = useApi();
  return useQuery({
    queryKey: queryKeys.moderation.detail(id ?? ''),
    queryFn: () => api.getModerationPersona(id!),
    enabled: id !== null,
  });
}

/**
 * O veredito do administrador. As duas famílias caem juntas porque um veredito
 * muda as duas: a linha sai (ou entra) na fila filtrada, e a persona entra (ou
 * sai) do roster que o seletor do chat do DONO lê.
 */
function useModerationWriteResult(): () => void {
  const qc = useQueryClient();
  return () => {
    void qc.invalidateQueries({ queryKey: queryKeys.moderation.all });
    void qc.invalidateQueries({ queryKey: queryKeys.agents.all });
  };
}

export function useApprovePersona(): UseMutationResult<ModerationItem, Error, string> {
  const api = useApi();
  const settle = useModerationWriteResult();
  return useMutation({
    mutationFn: (id: string) => api.approvePersona(id),
    onSuccess: settle,
  });
}

export function useRejectPersona(): UseMutationResult<
  ModerationItem,
  Error,
  { id: string; reason: string }
> {
  const api = useApi();
  const settle = useModerationWriteResult();
  return useMutation({
    mutationFn: ({ id, reason }: { id: string; reason: string }) => api.rejectPersona(id, reason),
    onSuccess: settle,
  });
}

// ---- WhatsApp link ---------------------------------------------------------

/**
 * The caller's own link. Never cached beyond the session and never stale for
 * long: linking and unlinking both write straight into this key, so the section
 * re-renders from the server's answer rather than from a guess.
 */
export function useWhatsAppLink(): UseQueryResult<WhatsAppLink> {
  const api = useApi();
  return useQuery({
    queryKey: queryKeys.whatsappLink,
    queryFn: () => api.getWhatsAppLink(),
    staleTime: 60_000,
  });
}

/**
 * Ask for a code. Deliberately does NOT touch the link cache: nothing is linked
 * until the code comes back, and optimistically flipping the section to "linked"
 * would be the one lie this whole flow exists to prevent.
 */
export function useStartWhatsAppLink(): UseMutationResult<WhatsAppLinkStarted, Error, string> {
  const api = useApi();
  return useMutation({ mutationFn: (phone: string) => api.startWhatsAppLink(phone) });
}

export function useVerifyWhatsAppLink(): UseMutationResult<WhatsAppLink, Error, string> {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (code: string) => api.verifyWhatsAppLink(code),
    onSuccess: (link) => {
      // The verification answers with the whole state, so seeding is enough.
      qc.setQueryData(queryKeys.whatsappLink, link);
      // It also REACTIVATES the groups an earlier unlink suspended, and that
      // happens server-side where this page cannot see it: without the
      // invalidation the section comes back showing every group as "Suspenso"
      // until the cache goes stale on its own.
      qc.invalidateQueries({ queryKey: queryKeys.whatsappGroups });
    },
  });
}

export function useDeleteWhatsAppLink(): UseMutationResult<void, Error, void> {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api.deleteWhatsAppLink(),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.whatsappLink });
      // Unlinking SUSPENDS the caller's groups server-side, so the list beside
      // it is stale the moment this succeeds — every badge changes.
      qc.invalidateQueries({ queryKey: queryKeys.whatsappGroups });
    },
  });
}

// ---- WhatsApp groups -------------------------------------------------------

export function useWhatsAppGroups(): UseQueryResult<WhatsAppGroups> {
  const api = useApi();
  return useQuery({
    queryKey: queryKeys.whatsappGroups,
    queryFn: () => api.listWhatsAppGroups(),
    staleTime: 60_000,
  });
}

/**
 * Ask for a registration code.
 *
 * The list is re-READ on success and never written to, and the difference is the
 * whole honesty of this ceremony: a code registers nothing until somebody posts
 * it inside a group, which happens in WhatsApp and not here, so nothing may be
 * added optimistically. Re-reading is worth it for the opposite reason — the
 * cached list is quite likely stale by now, because the other door into this
 * flow is a DM (`registrar grupo`), and a group registered from a phone five
 * minutes ago would otherwise be missing from the page the person is about to
 * act on.
 */
export function useIssueWhatsAppGroupCode(): UseMutationResult<
  WhatsAppGroupCode,
  Error,
  WhatsAppGroupAgentsChoice
> {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (agents: WhatsAppGroupAgentsChoice) => api.issueWhatsAppGroupCode(agents),
    onSuccess: () => qc.invalidateQueries({ queryKey: queryKeys.whatsappGroups }),
  });
}

/**
 * Os agentes que o dono pode pôr num grupo. Só busca quando o seletor abre
 * (`enabled`); um agente pessoal criado há um minuto precisa estar lá, e por
 * isso toda escrita em "Meus agentes" invalida esta chave.
 */
export function useWhatsAppGroupAgentCandidates(
  enabled: boolean,
): UseQueryResult<WhatsAppGroupAgentCandidates> {
  const api = useApi();
  return useQuery({
    queryKey: queryKeys.whatsappGroupAgents,
    queryFn: () => api.listWhatsAppGroupAgentCandidates(),
    enabled,
  });
}

export function useSetWhatsAppGroupAgents(): UseMutationResult<
  WhatsAppGroup,
  Error,
  { groupId: string; agents: WhatsAppGroupAgentsChoice }
> {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ groupId, agents }) => api.setWhatsAppGroupAgents(groupId, agents),
    onSuccess: () => qc.invalidateQueries({ queryKey: queryKeys.whatsappGroups }),
  });
}

export function useDeleteWhatsAppGroup(): UseMutationResult<void, Error, string> {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (groupId: string) => api.deleteWhatsAppGroup(groupId),
    onSuccess: () => qc.invalidateQueries({ queryKey: queryKeys.whatsappGroups }),
  });
}

// ---- Dependentes (família) -------------------------------------------------

export function useMyDependents(): UseQueryResult<DependentsResponse> {
  const api = useApi();
  return useQuery({
    queryKey: queryKeys.myDependents.all,
    queryFn: () => api.listMyDependents(),
    staleTime: 60_000,
  });
}

/**
 * O que toda escrita sobre um dependente invalida: a lista.
 *
 * Ela carrega o status, o número mascarado e a contagem de lembretes de cada
 * linha — três campos que o servidor recalcula. Escrever na cache no lugar de
 * reler traria de volta o defeito que o registro de grupos já teve: o console
 * mostrando um estado que só ele acredita.
 */
function useDependentInvalidation(): () => void {
  const qc = useQueryClient();
  return () => {
    void qc.invalidateQueries({ queryKey: queryKeys.myDependents.all });
  };
}

export function useCreateMyDependent(): UseMutationResult<Dependent, Error, DependentCreate> {
  const api = useApi();
  const invalidate = useDependentInvalidation();
  return useMutation({
    mutationFn: (input: DependentCreate) => api.createMyDependent(input),
    onSuccess: invalidate,
  });
}

export function useUpdateMyDependent(): UseMutationResult<
  Dependent,
  Error,
  { id: string; patch: DependentPatch }
> {
  const api = useApi();
  const invalidate = useDependentInvalidation();
  return useMutation({
    mutationFn: ({ id, patch }) => api.updateMyDependent(id, patch),
    onSuccess: invalidate,
  });
}

export function useDeleteMyDependent(): UseMutationResult<void, Error, string> {
  const api = useApi();
  const qc = useQueryClient();
  const invalidate = useDependentInvalidation();
  return useMutation({
    mutationFn: (id: string) => api.deleteMyDependent(id),
    onSuccess: (_void, id) => {
      // Os lembretes vão junto no servidor (CASCADE), então a entrada cacheada
      // deles passa a descrever uma linha que não existe mais.
      qc.removeQueries({ queryKey: queryKeys.myDependents.reminders(id) });
      invalidate();
    },
  });
}

/**
 * Pede um código de vínculo.
 *
 * A lista é RELIDA no sucesso e nunca escrita, pelo mesmo motivo do código de
 * grupo: um código não vincula nada até o dependente mandá-lo ao agente, o que
 * acontece no WhatsApp e não aqui. Reler ainda é o certo porque a outra ponta do
 * fluxo é justamente essa — um vínculo consumado cinco minutos atrás só chega ao
 * console quando ele volta a perguntar.
 */
export function useIssueDependentCode(): UseMutationResult<DependentCode, Error, string> {
  const api = useApi();
  const invalidate = useDependentInvalidation();
  return useMutation({
    mutationFn: (id: string) => api.issueDependentCode(id),
    onSuccess: invalidate,
  });
}

/** Os lembretes de UM dependente; sem dependente aberto não há o que pedir. */
export function useDependentReminders(
  dependentId: string | null,
): UseQueryResult<DependentRemindersResponse> {
  const api = useApi();
  return useQuery({
    queryKey: queryKeys.myDependents.reminders(dependentId ?? ''),
    queryFn: () => api.listDependentReminders(dependentId!),
    enabled: dependentId !== null,
    staleTime: 30_000,
  });
}

export function useCreateDependentReminder(): UseMutationResult<
  DependentReminder,
  Error,
  { dependentId: string; input: DependentReminderCreate }
> {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ dependentId, input }) => api.createDependentReminder(dependentId, input),
    onSuccess: (_reminder, { dependentId }) => {
      void qc.invalidateQueries({ queryKey: queryKeys.myDependents.reminders(dependentId) });
      // A linha do dependente mostra QUANTOS lembretes ele tem: sem isto o
      // contador fica atrás do Dialog que o leitor tem aberto na frente.
      void qc.invalidateQueries({ queryKey: queryKeys.myDependents.all });
    },
  });
}

export function useDeleteDependentReminder(): UseMutationResult<
  void,
  Error,
  { dependentId: string; reminderId: string }
> {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ dependentId, reminderId }) =>
      api.deleteDependentReminder(dependentId, reminderId),
    onSuccess: (_void, { dependentId }) => {
      void qc.invalidateQueries({ queryKey: queryKeys.myDependents.reminders(dependentId) });
      void qc.invalidateQueries({ queryKey: queryKeys.myDependents.all });
    },
  });
}

/** Um turno do widget público do site. Nada a invalidar: a conversa não é gravada. */
export function useSiteChat(): UseMutationResult<SiteChatReply, Error, SiteChatInput> {
  const api = useApi();
  return useMutation({ mutationFn: (input: SiteChatInput) => api.siteChat(input) });
}

interface TranscribeSiteAudioVariables extends TranscribeAudioVariables {
  /** O id da conversa da aba: o limite por sessão do backend. */
  sessionId: string;
}

/** O clipe do microfone do widget público → texto. Anônimo, e nada a invalidar. */
export function useTranscribeSiteAudio(): UseMutationResult<
  TranscriptionResult,
  Error,
  TranscribeSiteAudioVariables
> {
  const api = useApi();
  return useMutation({
    mutationFn: ({ audio, mimetype, sessionId }: TranscribeSiteAudioVariables) =>
      api.transcribeSiteAudio(audio, mimetype, sessionId),
  });
}

// ---- Billing -----------------------------------------------------------------

/** A cadência de releitura de uma assinatura pendente: a baixa do Pix acontece
 *  fora da página, e o QR expira (o servidor o relê a cada GET). */
export const BILLING_PENDING_POLL_MS = 5000;

/**
 * Se a assinatura deve continuar sendo relida: só enquanto `pending_payment`.
 * Extraída (como {@link ingestionRunsPollInterval}) para a cadência ser
 * testável sem montar a query.
 */
export function billingSubscriptionPollInterval(status: string | undefined): number | false {
  return status === 'pending_payment' ? BILLING_PENDING_POLL_MS : false;
}

/**
 * O catálogo público de planos. `staleTime` alto: o preço é dado da seed do
 * servidor e não muda dentro de uma sessão — e a landing, o /planos e o
 * checkout leem a MESMA chave, então uma visita só pede uma vez.
 */
export function useBillingPlans(): UseQueryResult<BillingPlansResponse> {
  const api = useApi();
  return useQuery({
    queryKey: queryKeys.billingPlans,
    queryFn: () => api.listBillingPlans(),
    staleTime: 300_000,
  });
}

/** A assinatura da conta, com polling enquanto um Pix estiver aguardando baixa. */
export function useBillingSubscription(): UseQueryResult<BillingSubscription> {
  const api = useApi();
  return useQuery({
    queryKey: queryKeys.billingSubscription,
    queryFn: () => api.getBillingSubscription(),
    staleTime: 30_000,
    refetchInterval: (query) => billingSubscriptionPollInterval(query.state.data?.status),
  });
}

/** O extrato real de cobranças da conta, sem dados do cartão nem payload bruto. */
export function useBillingPayments(): UseQueryResult<BillingPaymentsResponse> {
  const api = useApi();
  return useQuery({
    queryKey: queryKeys.billingPayments,
    queryFn: () => api.listBillingPayments(),
    staleTime: 30_000,
  });
}

/**
 * A contratação. A resposta É a visão da assinatura, então ela semeia o cache
 * (a tela confirma sem esperar outra leitura); a invalidação em seguida relê os
 * dois lados que a contratação muda — o `me` (as capabilities do plano) e a
 * própria assinatura, cujo QR Pix é fresco a cada GET.
 */
export function useStartCheckout(): UseMutationResult<
  BillingSubscription,
  Error,
  BillingCheckoutInput
> {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: BillingCheckoutInput) => api.startBillingCheckout(payload),
    onSuccess: (subscription) => {
      qc.setQueryData(queryKeys.billingSubscription, subscription);
      void qc.invalidateQueries({ queryKey: queryKeys.me });
      void qc.invalidateQueries({ queryKey: queryKeys.billingSubscription });
    },
  });
}

/** O cancelamento, no mesmo desenho do checkout: semeia a visão devolvida e relê. */
export function useCancelSubscription(): UseMutationResult<BillingSubscription, Error, void> {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api.cancelBillingSubscription(),
    onSuccess: (subscription) => {
      qc.setQueryData(queryKeys.billingSubscription, subscription);
      void qc.invalidateQueries({ queryKey: queryKeys.me });
      void qc.invalidateQueries({ queryKey: queryKeys.billingSubscription });
    },
  });
}

/** O painel "minhas indicações" da conta. */
export function useBillingReferrals(): UseQueryResult<BillingReferrals> {
  const api = useApi();
  return useQuery({
    queryKey: queryKeys.billingReferrals,
    queryFn: () => api.getBillingReferrals(),
    staleTime: 60_000,
  });
}

/** Anexa o código de quem indicou; a resposta (o painel) semeia o cache. */
export function useAttachReferral(): UseMutationResult<BillingReferrals, Error, string> {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (code: string) => api.attachBillingReferral(code),
    onSuccess: (referrals) => qc.setQueryData(queryKeys.billingReferrals, referrals),
  });
}
